import React, { useState, ChangeEvent, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { StatusCode } from "api/protocols";
import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import axios, { CancelToken } from "axios";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import {
  GetProductGroups,
  GetProductsInGroup,
  PostProductGroup,
  TPostProductGroup,
} from "api/rpc/2024-04/facilityAdmin/product/groups";
import { Select } from "components/select/index";
import { IProduct } from "redux/reducers/models/product";
import Popup from "components/popup/Popup";
import { useTranslation, Trans } from "react-i18next";
import { IProductGroup, IProductInGroup } from "redux/reducers/models/groups";

interface IFilterState {
  parentQuery: string;
  parentSearching: boolean;
  productGroups: IProductGroup[];
  openWarningModal: boolean;
}

interface IGroup {
  title: string;
  type: string;
  parent_id: number;
  addedProducts?: IProductInGroup[];
}

export default function ProductGroupNew() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { Option } = Select;
  const { t, i18n } = useTranslation();
  const [group, setGroup] = useState<IGroup>({
    title: "",
    type: "",
    parent_id: null,
    addedProducts: [],
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    parentQuery: "",
    parentSearching: false,
    productGroups: [],
    openWarningModal: false,
  });

  const types = [
    {
      title: t("Category"),
      value: "group",
    },
    {
      title: t("Subcategory"),
      value: "sub_group",
    },
  ];

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadProductGroups(source.token);
    return () => source.cancel();
  }, []);

  async function loadProductGroups(token?: CancelToken) {
    if (filterState.productGroups !== undefined) {
      setFilterState(prevState => ({ ...prevState, productGroups: undefined }));
    }

    const res = await GetProductGroups(null, token ? false : true, token);

    if (res.status !== StatusCode.OK) {
      setFilterState(prevState => ({ ...prevState, productGroups: [] }));
      return;
    }

    setFilterState(prevState => ({ ...prevState, productGroups: res.data }));
  }

  async function addProductGroup() {
    const params: TPostProductGroup = {
      title: group.title,
      type: group.type,
      parent_id: group.parent_id,
    };

    const res = await PostProductGroup(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(res.data ? res.data : t("There was an error adding the new product group.")));
      return;
    }

    history.push("/admin/settings/groups");
  }

  function handleDropDownChange(value: string) {
    setGroup(prevState => ({ ...prevState, type: value }));
  }

  async function handleParentChange(value: string) {
    const productsInGroupRes = await GetProductsInGroup({ group_id: Number(value) }, true);

    if (productsInGroupRes.status !== StatusCode.OK) {
      dispatch(showError(t("There was an error checking for products in group")));
      return;
    }

    setGroup(prevState => ({ ...prevState, parent_id: Number(value), addedProducts: productsInGroupRes.data }));
  }

  function checkForAddedProducts() {
    if (group.addedProducts.length > 0) {
      setFilterState(prevState => ({ ...prevState, openWarningModal: true }));
    } else {
      void addProductGroup();
    }
  }

  function closeWarningModal() {
    setFilterState(prevState => ({ ...prevState, openWarningModal: false }));
    setGroup(prevState => ({ ...prevState, parent_id: null }));
  }

  return (
    <Page
      title={t("New Product Category")}
      breadcrumbs={[{ prefix: true, label: t("Categories"), url: "/admin/settings/groups" }]}
      primaryAction={{
        action: () => checkForAddedProducts(),
        content: t("Save"),
        // disabled:  group.title.length === 0 || group.type.length === 0,
        disabled:
          group.title.length === 0 || group.type.length === 0
            ? true
            : group.type === "sub_group" && group.parent_id === null,
      }}
    >
      <Card>
        <Card.Section>
          <div className="mb-3">
            <Input
              label={t("Title")}
              value={group.title}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setGroup(prev => ({ ...prev, title: e.target.value }))}
            />
          </div>

          <Select
            label={t("Category Type")}
            onChange={handleDropDownChange}
            defaultValue={group.type}
          >
            {types.map((type, index) => {
              return (
                <Option key={index} value={type.value} name={type.title}>
                  <div style={{ display: "flex", alignItems: "center" }}>{type.title}</div>
                </Option>
              );
            })}
          </Select>

          {group.type?.toLocaleLowerCase() === "sub_group" ? (
            <div className="mt-3 mb-3">
              <Select
                showSearch
                label={t("Parent Category")}
                onSearch={(query: string) => setFilterState(prevState => ({ ...prevState, parentQuery: query }))}
                onChange={(value: any) => handleParentChange(value)}
                placeholder={t("Parent Category")}
                allowClear
                handleClear={() => setGroup(prev => ({ ...prev, parent_id: null }))}
                searchValue={filterState.parentQuery}
                showDropDownOnFocus={true}
                searching={filterState.parentSearching}
              >
                {filterState.productGroups
                  ?.filter(
                    (group: { title: string; type: string }) =>
                      group.title.toLocaleLowerCase().includes(filterState.parentQuery.toLocaleLowerCase()) &&
                      group.type === "group",
                  )
                  .map((group, index) => {
                    return (
                      <Select.Option key={index} value={group.id} name={group.title}>
                        <div className="flex items-center">{group.title}</div>
                      </Select.Option>
                    );
                  })}
              </Select>
            </div>
          ) : null}
        </Card.Section>
      </Card>

      <Popup
        open={filterState.openWarningModal}
        onCancel={closeWarningModal}
        onOk={addProductGroup}
        type="warning"
        title={t("Add new subcategory?")}
        description={t("The selected parent category contains products. Adding this subcategory will move the products to this new subcategory. Do you want to continue?")}
      />
    </Page>
  );
}
